import React, { useState, useRef } from "react";
import { CSDLogo } from "../../../assets/data/Imagedata";
import html2canvas from "html2canvas";
import NationalPresidentSign from "./NationalPresidentSign.png";
import BACKIMAGE from "./BACKIMAGE.jpeg";
const IdCardSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const defaultPhoto = "https://crcbharat.com/images/default-photo.png";

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query) return;

    setLoading(true);
    try {
      const res = await fetch(
        `https://crcbharat.com/apis/donations/search_id_cards.php?q=${encodeURIComponent(
          query
        )}`
      );
      const data = await res.json();
      setResults(data.members || []);
    } catch (err) {
      console.error(err);
      alert("Something went wrong!");
    }
    setLoading(false);
  };

  // Function to convert image to base64
  const imageToBase64 = async (url) => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error("Base64 conversion failed:", e);
      return null;
    }
  };

  // Convert local images to base64
  const convertLocalImageToBase64 = async (imagePath) => {
    try {
      const response = await fetch(imagePath);
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Local image conversion failed:", error);
      return imagePath; // Return original path as fallback
    }
  };

  const downloadIdCard = async (member, index) => {
    setDownloading(index);
    try {
      const frontElement = document.getElementById(
        `idcard-front-${member.id_card_number}`
      );
      const backElement = document.getElementById(
        `idcard-back-${member.id_card_number}`
      );

      // Get all images and convert them properly
      const frontImgElements = frontElement.querySelectorAll("img");
      const backImgElements = backElement.querySelectorAll("img");
      const allImgElements = [...frontImgElements, ...backImgElements];
      const originalSrcs = [];

      // Convert all images to base64 to avoid CORS issues
      for (let imgElement of allImgElements) {
        originalSrcs.push(imgElement.src);

        if (imgElement.alt === "CSD Logo") {
          // Convert CSD logo to base64
          try {
            const base64Logo = await convertLocalImageToBase64(CSDLogo);
            imgElement.src = base64Logo;
          } catch (error) {
            console.warn("Could not convert CSD logo:", error);
          }
        } else if (imgElement.alt === "national president signature") {
          // Convert signature to base64
          try {
            const base64Signature = await convertLocalImageToBase64(
              NationalPresidentSign
            );
            imgElement.src = base64Signature;
          } catch (error) {
            console.warn("Could not convert signature:", error);
          }
        } else if (imgElement.alt === "Member Photo") {
          // Convert member photo to base64
          const photoUrl =
            member.photo_url && !member.photo_url.includes("default-photo.png")
              ? member.photo_url
              : defaultPhoto;

          try {
            const base64Image = await imageToBase64(photoUrl);
            if (base64Image) {
              imgElement.src = base64Image;
            }
          } catch (error) {
            console.warn("Could not convert member photo:", error);
          }
        }

        // Wait for image to load
        await new Promise((resolve) => {
          imgElement.onload = resolve;
          imgElement.onerror = resolve;
          setTimeout(resolve, 500);
        });
      }

      // Create canvas for front side
      const frontCanvas = await html2canvas(frontElement, {
        scale: 3,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
      });

      // Create canvas for back side
      const backCanvas = await html2canvas(backElement, {
        scale: 3,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
      });

      // Create a combined canvas for both sides
      const combinedCanvas = document.createElement("canvas");
      const ctx = combinedCanvas.getContext("2d");

      // Set canvas dimensions (both sides stacked vertically with gap)
      const gap = 20;
      combinedCanvas.width = Math.max(frontCanvas.width, backCanvas.width);
      combinedCanvas.height = frontCanvas.height + backCanvas.height + gap;

      // Fill with white background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

      // Draw front side (top)
      ctx.drawImage(frontCanvas, 0, 0);

      // Draw back side (bottom)
      ctx.drawImage(backCanvas, 0, frontCanvas.height + gap);

      const image = combinedCanvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      link.href = image;
      link.download = `CRC_ID_Card_${
        member.id_card_number
      }_${member.name.replace(/\s+/g, "_")}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Restore original image sources
      allImgElements.forEach((imgElement, i) => {
        imgElement.src = originalSrcs[i];
      });
    } catch (error) {
      console.error("Error downloading ID card:", error);
      alert("Error downloading ID card. Please try again.");
    }
    setDownloading(null);
  };

  // Filter results to only show paid members
  const paidMembers = results.filter(
    (member) => member.status?.toLowerCase() === "paid"
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 py-8 flex flex-col items-center">
      <div className="max-w-6xl w-full bg-white rounded-2xl shadow-xl p-6 md:p-8 transition-all duration-300">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-blue-900 mb-3">
            Search CRC ID Cards
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Find and download your Consumer Rights Council ID card using your
            email, phone number, or ID card number
          </p>
        </div>

        {/* Search Form */}
        <form
          onSubmit={handleSearch}
          className="flex flex-col sm:flex-row gap-3 mb-8"
        >
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by Email, Phone or ID Card Number"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all duration-200 shadow-sm"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none transition-all duration-200 font-semibold shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Searching...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                Search
              </>
            )}
          </button>
        </form>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <div className="inline-flex items-center gap-3 text-blue-600">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-lg font-medium">
                Searching for ID cards...
              </span>
            </div>
          </div>
        )}

        {/* Results Grid */}
        {!loading && paidMembers.length > 0 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-800">
                Found {paidMembers.length} ID Card
                {paidMembers.length > 1 ? "s" : ""}
              </h2>
              <p className="text-gray-600 mt-2">
                Click "Download ID Card" to save your ID card as PNG image
                (Front + Back)
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {paidMembers.map((member, index) => (
                <div
                  key={member.id_card_number}
                  className="bg-white rounded-xl shadow-md border border-blue-100 p-6 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="flex flex-col items-center space-y-4">
                    {/* ID CARD CONTAINER - VERTICAL STACK */}
                    <div className="flex flex-col items-center gap-6">
                      {/* FRONT SIDE */}
                      <div className="flex flex-col items-center">
                        <h3 className="text-sm font-semibold text-blue-800 mb-2">
                          Front Side
                        </h3>
                        <div
                          id={`idcard-front-${member.id_card_number}`}
                          className="overflow-hidden bg-blue-50 border-2 border-blue-300"
                          style={{
                            width: "337px",
                            height: "213px",
                            display: "flex",
                            flexDirection: "column",
                            fontFamily: "Arial, sans-serif",
                          }}
                        >
                          {/* HEADER */}
                          <div className="flex items-center bg-blue-900 p-1">
                            <img
                              src={CSDLogo}
                              alt="CSD Logo"
                              className="w-10 h10 object-contain flex-shrink-0"
                              crossOrigin="anonymous"
                            />
                            <div className="flex flex-col justify-center text-center w-full leading-none">
                              <h1 className="text-white font-bold text-[14px] uppercase tracking-tight mb-0.5">
                                CONSUMER RIGHTS COUNCIL
                              </h1>

                              <div className="text-yellow-400 text-[10px] font-semibold my-1">
                                उपभोक्ता अधिकार परिषद्
                              </div>
                            </div>
                          </div>

                          {/* MIDDLE SECTION */}
                          <div className="flex flex-1 p-2 gap-2 ">
                            {/* PHOTO SECTION */}
                            <div className="flex flex-col items-center">
                              <span className="text-[9px] text-gray-800 z-[10]">
                                {member.id_card_number}
                              </span>
                              <div className="w-16 h-20 border mt-2 border-blue-400 overflow-hidden bg-gray-100">
                                <img
                                  src={member.photo_url || defaultPhoto}
                                  alt="Member Photo"
                                  onError={(e) => (e.target.src = defaultPhoto)}
                                  crossOrigin="anonymous"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </div>

                            {/* DETAILS SECTION */}
                            <div className="flex-1 text-[9px] relative">
                              <div className="flex">
                                <strong className="text-blue-800 w-16">
                                  Name:
                                </strong>
                                <span className="text-gray-800">
                                  {member.name}
                                </span>
                              </div>
                              <div className="flex">
                                <strong className="text-blue-800 w-16">
                                  Father Name:
                                </strong>
                                <span className="text-gray-800">
                                  {member.father_name}
                                </span>
                              </div>

                              <div className="flex">
                                <strong className="text-blue-800 w-16">
                                  Designation:
                                </strong>
                                <span className="text-gray-800">
                                  {member.designation || member.membership_type}
                                </span>
                              </div>
                              <div className="flex">
                                <strong className="text-blue-800 w-16">
                                  Date of Birth:
                                </strong>
                                <span className="text-gray-800">
                                  {member.date_of_birth
                                    ? member.date_of_birth
                                        .split("-")
                                        .reverse()
                                        .join("-")
                                    : "N/A"}
                                </span>
                              </div>

                              <div className="flex">
                                <strong className="text-blue-800 w-16">
                                  Phone:
                                </strong>
                                <span className="text-gray-800">
                                  {member.phone}
                                </span>
                              </div>
                              <div className="flex">
                                <strong className="text-blue-800 w-16">
                                  Blood Group:
                                </strong>
                                <span className="text-gray-800">
                                  {member.blood_group || "N/A"}
                                </span>
                              </div>

                              {/* SIGNATURE ON RIGHT SIDE */}
                              <div className="absolute bottom-0 right-0 pb-1 pr-1 text-center">
                                <img
                                  src={NationalPresidentSign}
                                  alt="national president signature"
                                  className="w-[60px] h-[60px] rounded-lg object-contain mx-auto"
                                  crossOrigin="anonymous"
                                />
                                <span className="text-blue-800 text-[7px] block leading-tight">
                                  National President
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* FOOTER */}
                          <div className="bg-blue-800 text-white p-1 items-center grid grid-cols-3">
                            <div className="text-[8px]">
                              <strong>Work Permit</strong>
                              <div className="text-[7px] text-yellow-300">
                                All India
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-[8px] font-semibold">
                                IDENTITY CARD
                              </div>
                              <div className="text-[7px] text-yellow-300">
                                Valid Till {member.validity || "31-12-2026"}
                              </div>
                            </div>
                            <div className="text-[8px] text-right">
                              <strong>Powered by</strong>
                              <div className="text-[7px] text-yellow-300">
                                www.crcbharat.com
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* BACK SIDE */}
                      <div className="flex flex-col items-center">
                        <h3 className="text-sm font-semibold text-blue-800 mb-2">
                          Back Side
                        </h3>
                        <div
                          id={`idcard-back-${member.id_card_number}`}
                          className="overflow-hidden bg-blue-50 border-2 border-blue-300 relative"
                          style={{
                            width: "337px",
                            height: "213px",
                            display: "flex",
                            flexDirection: "column",
                            fontFamily: "Arial, sans-serif",
                          }}
                        >
                          {/* BACK CONTENT */}
                          <img
                            src={BACKIMAGE}
                            alt="idbackiamge"
                            className=" object-cover "
                          />
                        </div>
                      </div>
                    </div>

                    {/* DOWNLOAD BUTTON */}
                    <div className="flex flex-col items-center gap-3">
                      <button
                        onClick={() => downloadIdCard(member, index)}
                        disabled={downloading === index}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {downloading === index ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            Download Both Sides
                          </>
                        )}
                      </button>

                      <p className="text-xs text-gray-500 text-center max-w-sm">
                        Download includes both front and back sides of your ID
                        card stacked vertically
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Paid Members Found */}
        {!loading && results.length > 0 && paidMembers.length === 0 && (
          <div className="text-center py-12 bg-yellow-50 rounded-xl border border-yellow-200">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-yellow-800 mb-2">
              No Active ID Cards Found
            </h3>
            <p className="text-yellow-700 max-w-md mx-auto">
              We found your record but no active ID cards. Please check if your
              payment has been completed or contact support.
            </p>
          </div>
        )}

        {/* No Results Found */}
        {!loading && results.length === 0 && query && (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No Results Found
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              No ID cards found for your search. Please check your search terms
              and try again.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdCardSearch;
